import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './Contexts/AuthContext';
import { ToastProvider } from './Contexts/ToastContext';
import ToastContainer from './Components/ToastContainer';
import Header from './Header';
import Footer from './Footer/Footer';
import BookingBag from './ShoppingCart/BookingBag';
import LoginForm from './Authentication/LoginForm';
import Booking from './Booking/Booking';
import CampSiteDetails from './Booking/CampSiteDetails';
import CampSiteManager from './CRUD/CampSites/CampSiteManager';
import CartPage from './ShoppingCart/CartPage';
import CheckoutPage from './ShoppingCart/CheckoutPage';
import PaymentSuccess from './ShoppingCart/Payments/PaymentSuccess';
import ProtectedRoute from './Authentication/ProtectedRoute';
import BusinessUserManager from './BusinessUsers/CRUD/BusinessUserManager';
import TestFetchBookingsByOrderNumber from './CRUD/ManageBookings/TestFetchBookingsByOrderNumber';
import NewBookingManager from './CRUD/ManageBookings/NewBookingManager';
import ManagerDashboard from './CRUD/ManagerDashboard';
import PrivateUsersManager from './PrivateUsers/CRUD/PrivateUsersManager';
import CampSiteForm from './CRUD/CampSites/CampSiteForm';
import ManageAdminUsers from './AdminUsers/SuperAdminDashboard';
import AdminManager from './AdminUsers/AdminManagement';
import AdminSettings from './AdminUsers/AdminSettings';
import ResetPassword from './AdminUsers/ResetPassword';
import ForgotPassword from './AdminUsers/ForgotPassword';
import Verify2FA from './Authentication/Verify2FA';
import AdminDiagnostics from './AdminUsers/AdminDiagnostics';
import DetailedChaletBookings from './CRUD/ManageBookings/DetailedChaletBookings';
import DebugAvailability from './CRUD/ManageBookings/DebugAvailability';
import PackageForm from './TourPackages/PackageForm';
import CategoryManager from './Categories/CategoryManager';
import PackageBooking from './Booking/PackageBooking';
import CategoriesBooking from './Booking/TourPackages/CategoriesBooking';
import PackageManager from './Booking/TourPackages/CRUD/PackageManager';
import NewCartPage from './ShoppingCart/NewCartPage';
import BookingConfirmation from './CRUD/ManageBookings/BookingConfirmation';
import PrivacyPolicy from './Footer/PrivacyPolicy';
import PreBookingConfirmationPage from './PreBookingConfirmationPage';
import ManagerCalendarViewWrapper from './Authentication/ManagerCalendarViewWrapper';

const App: React.FC = () => {
  const [cartItems, setCartItems] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const { isAuthenticated, logout, user } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);

  const generateSessionId = () => 'xxxx-xxxx-xxxx-xxxx'.replace(/[x]/g, () => ((Math.random() * 16) | 0).toString(16));

  useEffect(() => {
    let storedSessionId = localStorage.getItem('sessionId');
    if (!storedSessionId) {
      storedSessionId = generateSessionId();
      localStorage.setItem('sessionId', storedSessionId);
    }
    setSessionId(storedSessionId);
  }, []);

  const fetchCartItems = useCallback(async (): Promise<any[]> => {
    if (!sessionId) return [];
    try {
      const response = await axios.get<{ items: any[] }>(`${process.env.REACT_APP_API_URL}/ShoppingCart/getShoppingCart`, { params: { sessionId } });
      if (response.data?.items) {
        setCartItems(response.data.items);
        return response.data.items;
      }
    } catch (error: any) {
      if (error.response?.status !== 404) console.error('Error fetching cart items:', error);
    }
    return [];
  }, [sessionId]);

  useEffect(() => { fetchCartItems(); }, [fetchCartItems]);

  const isAdmin = () => user ? user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'Admin' : false;
  const isSuperAdmin = () => user ? user['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'SuperAdmin' : false;
  
  const getTitle = (pathname: string) => {
    const titles: Record<string, string> = {
      '/login': 'Manager Login',
      '/manager': 'Manager Dashboard',
      '/managerCalendarView': 'Calendar View',
      '/bookingManager': 'Manage Bookings',
      '/newPackageManager': 'Manage Packages',
      '/businessUserManager': 'Manage Business Users',
      '/privateUserManager': 'Manage Private Users',
      '/categoryManager': 'Category Manager',
      '/adminManager': 'Admin Manager',
      '/adminSettings': 'Admin Settings',
      '/adminDiagnostics': 'Admin Diagnostics',
      '/chaletBookingLookup': 'Chalet Booking Lookup',
      '/debug-availability': 'Debug Availability',
      '/superadmin': 'Super Admin Dashboard',
    };
    return titles[pathname] || 'Rajamaa in Lapland';
  };

  // Inner component to access useLocation within Router context
  const HeaderWithLocation: React.FC = () => {
    const location = useLocation();
    return (
      <Header
        isAuthenticated={isAuthenticated}
        onLogout={logout}
        title={getTitle(location.pathname)}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
      />
    );
  };

  return (
    <ToastProvider>
      <Router>
        <div className='header-container'>
          {cartItems.length > 0 && (
            <BookingBag cartItems={cartItems} setCartItems={setCartItems} fetchCartItems={fetchCartItems} />
          )}
          <HeaderWithLocation />
        </div>
        <Routes>
          <Route path='/login' element={!isAuthenticated ? <LoginForm onLogin={() => {}} /> : (isAdmin() ? <Navigate to='/manager' /> : isSuperAdmin() ? <Navigate to='/superadmin' /> : <Navigate to='/' />)} />
          <Route path='/' element={isAuthenticated ? (isAdmin() ? <Navigate to='/manager' /> : isSuperAdmin() ? <Navigate to='/superadmin' /> : <Navigate to='/packageBooking' />) : <Navigate to='/packageBooking' />} />
          <Route path='/booking' element={<Booking cartItems={cartItems} setCartItems={setCartItems} dateRange={dateRange} setDateRange={setDateRange} />} />
          <Route path='/categoriesBooking' element={<CategoriesBooking />} />
          <Route path='/packageBooking' element={<PackageBooking dateRange={{ startDate: dateRange.startDate || new Date(), endDate: dateRange.endDate || new Date() }} setDateRange={setDateRange} fetchCartItems={fetchCartItems} />} />
          <Route path='/campsite/:id' element={<CampSiteDetails cartItems={cartItems} setCartItems={setCartItems} dateRange={dateRange} />} />
          <Route path='/checkout' element={cartItems.length > 0 ? <CheckoutPage cartItems={cartItems} setCartItems={setCartItems} customerId={null} /> : <Navigate to='/' />} />
          <Route path='/payment-success' element={<PaymentSuccess />} />
          <Route path='/resetPassword' element={<ResetPassword />} />
          <Route path='/forgotPassword' element={<ForgotPassword />} />
          <Route path='/verify-2fa' element={<Verify2FA />} />
          <Route path='/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/bookingByOrderNumber' element={<TestFetchBookingsByOrderNumber />} />
          <Route path='/testNewBookingManager' element={<ProtectedRoute component={NewBookingManager} />} />
          <Route path='/cart' element={<CartPage cartItems={cartItems} setCartItems={setCartItems} />} />
          <Route path='/newCart' element={<NewCartPage cartItems={cartItems} setCartItems={setCartItems} />} />
          <Route path='/manager' element={<ProtectedRoute component={ManagerDashboard} />} />
          <Route path='/superadmin' element={<ProtectedRoute component={ManageAdminUsers} />} />
          <Route path='/campsites' element={<CampSiteManager />} />
          <Route path='/campsite/add' element={<CampSiteForm onSubmit={() => {}} onCancel={() => {}} />} />
          <Route path='/campsite/edit/:id' element={<CampSiteForm onSubmit={() => {}} onCancel={() => {}} />} />
          <Route path='/businessUserManager' element={<ProtectedRoute component={BusinessUserManager} />} />
          <Route path='/privateUserManager' element={<ProtectedRoute component={PrivateUsersManager} />} />
          <Route path='/bookingManager' element={<ProtectedRoute component={NewBookingManager} />} />
          <Route path='/adminManager' element={<ProtectedRoute component={AdminManager} />} />
          <Route path='/adminSettings' element={<ProtectedRoute component={AdminSettings} />} />
          <Route path='/adminDiagnostics' element={<ProtectedRoute component={AdminDiagnostics} />} />
          <Route path='/chaletBookingLookup' element={<ProtectedRoute component={DetailedChaletBookings} />} />
          <Route path='/debug-availability' element={<ProtectedRoute component={DebugAvailability} />} />
          <Route path='/newPackageManager' element={<ProtectedRoute component={PackageManager} />} />
          <Route path='/categoryManager' element={<ProtectedRoute component={CategoryManager} />} />
          <Route path='/managerCalendarView' element={<ProtectedRoute component={ManagerCalendarViewWrapper} />} />
          <Route path='/package/:id' element={<ProtectedRoute component={() => (
            <PackageForm
              categories={[]}
              isEditing={false}
              handlePackageSubmit={() => {}}
              imageUrls={[]}
              getRootProps={() => {}}
              getInputProps={() => {}}
              mainImageIndex={0}
              setMainImageIndex={() => {}}
              setImageUrls={() => {}}
              onSubmit={() => {}}
              onCancel={() => {}}
              packageDetails={{}}
              pricingOptions={[]}
              dateRanges={[]}
              setDateRanges={() => {}}
              setPackageDetails={() => {}}
              setPricingOptions={() => {}}
            />
          )} />} />
          <Route path='/package/edit/:id' element={<ProtectedRoute component={() => (
            <PackageForm
              categories={[]}
              isEditing={true}
              handlePackageSubmit={() => {}}
              imageUrls={[]}
              getRootProps={() => {}}
              getInputProps={() => {}}
              mainImageIndex={0}
              setMainImageIndex={() => {}}
              setImageUrls={() => {}}
              onSubmit={() => {}}
              onCancel={() => {}}
              packageDetails={{}}
              pricingOptions={[]}
              dateRanges={[]}
              setDateRanges={() => {}}
              setPackageDetails={() => {}}
              setPricingOptions={() => {}}
            />
          )} />} />
          <Route path='/booking-confirmation' element={<BookingConfirmation orderDetails={{}} />} />
          <Route path='/confirm-prebooking' element={<PreBookingConfirmationPage />} />
          <Route path='*' element={<Navigate to='/' />} />
        </Routes>
        <Footer />
        <ToastContainer />
      </Router>
    </ToastProvider>
  );
};

export default App;